import type { DashboardSearchKind } from './DashboardSearchKind'
import type { DashboardSearchResult } from './DashboardSearchResult'

/** `order:ORD-2026-1842` → `order`, `menu:7` → `menu`; else `default`. */
function kindFromApiId(id: string | number): DashboardSearchKind {
  if (typeof id !== 'string') return 'default'
  const prefix = id.split(':')[0]?.trim().toLowerCase()
  if (prefix == null || prefix === '') return 'default'
  if (prefix === 'orders') return 'order'
  if (prefix === 'stores') return 'store'
  if (prefix === 'users') return 'user'
  if (prefix === 'menus') return 'menu'
  if (prefix === 'brands') return 'brand'
  if (prefix === 'payouts') return 'payout'
  if (
    prefix === 'order' ||
    prefix === 'store' ||
    prefix === 'user' ||
    prefix === 'menu' ||
    prefix === 'brand'
  ) {
    return prefix
  }
  if (prefix === 'payout') return 'payout'
  return 'default'
}

function extractArray(raw: unknown): unknown[] {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    if (Array.isArray(o['data'])) return o['data']
    if (Array.isArray(o['results'])) return o['results']
    if (Array.isArray(o['items'])) return o['items']
  }
  return []
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return undefined
}

function pickUrl(obj: Record<string, unknown>): string | undefined {
  const direct = pickString(obj, ['url', 'href', 'link'])
  if (direct != null && direct !== '') return direct
  const path = obj['path']
  if (typeof path === 'string' && path.trim()) {
    const p = path.trim()
    return p.startsWith('/') ? p : `/${p}`
  }
  return undefined
}

function pickKind(raw: unknown): DashboardSearchKind | undefined {
  if (typeof raw !== 'string' || !raw.trim()) return undefined
  const k = raw.trim().toLowerCase()
  if (
    k === 'order' ||
    k === 'store' ||
    k === 'menu' ||
    k === 'brand' ||
    k === 'user' ||
    k === 'payout'
  ) {
    return k
  }
  if (k === 'orders') return 'order'
  if (k === 'stores') return 'store'
  if (k === 'menus') return 'menu'
  if (k === 'brands') return 'brand'
  if (k === 'users') return 'user'
  if (k === 'payouts') return 'payout'
  return undefined
}

function normalizeItem(entry: unknown): DashboardSearchResult | null {
  if (entry == null || typeof entry !== 'object') return null
  const obj = entry as Record<string, unknown>
  const id = obj['id']
  if (id == null || (typeof id !== 'string' && typeof id !== 'number')) return null
  const name =
    pickString(obj, ['name', 'title', 'label', 'display_name']) ??
    (typeof id === 'string' || typeof id === 'number' ? String(id) : '')
  if (!name) return null
  const url = pickUrl(obj)
  if (url == null || url === '') return null
  const category = pickString(obj, ['category'])
  const subtitle = pickString(obj, ['subtitle', 'description', 'secondary'])
  const kindFromFields = pickKind(obj['kind']) ?? pickKind(obj['type']) ?? pickKind(obj['resource'])
  const kindFromId = kindFromApiId(id)
  const kind: DashboardSearchKind =
    kindFromId !== 'default' ? kindFromId : (kindFromFields ?? 'default')
  const dedupeKey =
    typeof obj['dedupe_key'] === 'string' && obj['dedupe_key'].trim()
      ? obj['dedupe_key'].trim()
      : String(id)
  return {
    dedupeKey,
    id,
    name,
    url,
    ...(subtitle != null && subtitle !== '' ? { subtitle } : {}),
    ...(category != null && category !== '' ? { category } : {}),
    kind
  }
}

/** Maps common API envelope shapes into navigable dashboard search rows. */
export function normalizeDashboardSearchResults(raw: unknown): DashboardSearchResult[] {
  const out: DashboardSearchResult[] = []
  for (const entry of extractArray(raw)) {
    const row = normalizeItem(entry)
    if (row) out.push(row)
  }
  return out
}
